import { useContext, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import AuthContext from "src/providers/AuthProvider";
import * as amplitude from "@amplitude/analytics-browser";
import { useGetConfigQuery } from "src/store/features/api/generated";
import { setMaintenanceStatus } from "src/store/features/maintenance.slice";
import { useAppDispatch } from "src/store/store";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import { trackEvent } from "src/Amplitude";
import api from "src/store/features/api";
import { RootState, useAppDispatch as useAppDispatchStore, useAppSelector } from "src/store/store";
import { setLoginEmail } from "src/store/features/settings.slice";
import { useLoginRedirect } from "src/utils/hooks/useLoginRedirect";
import { getSessionItem, setSessionCookie } from "src/utils/SessionManagement";
import useTransmitRisk from "src/utils/hooks/useTransmitRisk";
import { setTurnstileToken, useTurnstileSlice } from "src/store/features/turnstile.slice";
import {
  useLoginFormInputSlice,
  setEmail,
  setEmailError,
  setPassword,
  setPasswordError,
  setShowPassword,
  setResetCode,
  setStep,
  setError,
  setIsNextLoading,
  setIsSubmitLoading,
  setFailedLogin,
  setSelectedValue,
  setDialog,
  setMagicLink,
  setLoading,
  setNoDefault,
  setIsSupported,
  setSdkReady,
  setTwoFACode,
  setTwoFAError,
  setIsTwoFALoading,
  setShowTwoFA,
  resetLoginForm,
} from "src/store/features/loginFormInput.slice";
import { getRuntimeConfig } from "src/getRuntimeConfig";

declare global {
  interface Window {
    dataLayer: any[];
    tsPlatform: any;
  }
}

export function useLogin() {
  const history = useHistory();
  const { logout, isAuthenticated } = useContext(AuthContext);
  const { data } = useGetConfigQuery();
  const reduxDispatch = useAppDispatch();
  const isLoggingOut = window.sessionStorage.getItem("isLogout");

  // Login context and state
  const { login, tokenExchange, webauthnLogin } = useContext(AuthContext);
  const { loginEmail } = useAppSelector((state: RootState) => state.settings);
  const dispatch = useAppDispatchStore();

  const mounted = useRef(false);
  const { executeTransmitRisk } = useTransmitRisk();
  const { turnstileToken } = useTurnstileSlice();
  const [setSelectedUser] = api.useSetSelectedUserMutation({ fixedCacheKey: "login-set-selected-user" });
  const [setDefaultUser] = api.useSetDefaultUserMutation({ fixedCacheKey: "login-set-default-user" });
  const [sendMagicLink] = api.useMagicLinkMutation({ fixedCacheKey: "login-send-magic-link" });
  const loginRedirect = useLoginRedirect();

  // Get state from Redux slice
  const {
    email,
    emailError,
    password,
    passwordError,
    showPassword,
    resetCode,
    step,
    error,
    isNextLoading,
    isSubmitLoading,
    failedLogin,
    selectedValue,
    dialog,
    magicLink,
    loading,
    noDefault,
    isSupported,
    sdkReady,
    twoFACode,
    twoFAError,
    isTwoFALoading,
    showTwoFA,
  } = useLoginFormInputSlice();

  // Get device type for tracking
  const isMobile = useIsMobile();
  const device = isMobile ? "mobile" : "desktop";

  // Transmit Security Platform setup
  async function loadTSPlatform() {
    if ((window as any).__tsPlatformInitialized) return;
    const config = {
      clientId:
        getRuntimeConfig().CNB_ENV === "main" || getRuntimeConfig().CNB_ENV === "release"
          ? "505f0d5.EXYdsornYJxbGI7JiOGFN.transmit"
          : "b34f3a4.guaw9yvxhkko4e32ionfa.transmit",
      webauthn: { serverPath: "https://api.transmitsecurity.io" },
      ido: { serverPath: "https://api.transmitsecurity.io/ido" },
    };
    try {
      await window.tsPlatform.initialize(config);
    } catch (firstError) {
      console.warn("[TransmitSDK] initialize() failed, retrying in 3s...", firstError);
      await new Promise((resolve) => setTimeout(resolve, 3000));
      await window.tsPlatform.initialize(config);
    }
    (window as any).__tsPlatformInitialized = true;
  }

  // Maintenance and logout effects
  useEffect(() => {
    if (data) {
      reduxDispatch(setMaintenanceStatus(data));
      if (!data.authcontroller) {
        history.push("/maintenance");
      }
    }
  }, [data, reduxDispatch, history]);

  useEffect(() => {
    // remove any intercom icon
    const intercomLauncher = document.getElementsByClassName("intercom-launcher");
    const intercomNamespace = document.getElementsByClassName("intercom-namespace");
    while (intercomLauncher.length > 0) {
      if (intercomLauncher[0].parentNode !== null) {
        intercomLauncher[0].parentNode.removeChild(intercomLauncher[0]);
      }
    }
    while (intercomNamespace.length > 0) {
      if (intercomNamespace[0].parentNode !== null) {
        intercomNamespace[0].parentNode.removeChild(intercomNamespace[0]);
      }
    }

    if (isLoggingOut === "true") {
      logout();
      setTimeout(() => {
        amplitude.reset();
      }, 100);
    }
  }, [isLoggingOut]);

  useEffect(() => {
    async function isDeviceSupported() {
      const result = await window.tsPlatform.webauthn.isPlatformAuthenticatorSupported();
      dispatch(setIsSupported(result));
    }

    if (sdkReady) {
      isDeviceSupported();
    }
  }, [sdkReady]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    const hasFullSession =
      sessionStorage.getItem("access_token") &&
      sessionStorage.getItem("refresh_token") &&
      sessionStorage.getItem("id_token");

    if (isAuthenticated && hasFullSession && !noDefault) {
      if (mounted.current) {
        dispatch(setLoading(true));
      }
      loginRedirect();
    } else if (!hasFullSession) {
      if (mounted.current) {
        dispatch(setLoading(false));
      }
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAuthenticated, noDefault, loginRedirect, dispatch]);

  useEffect(() => {
    mounted.current = true;
    loadTSPlatform()
      .then(() => {
        if (!mounted.current) return;
        dispatch(setError(""));
        dispatch(setSdkReady(true));
      })
      .catch((error) => {
        console.error("[TransmitSDK] initialize() failed after retry", error);
      });

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    if (
      queryParams.has("code") &&
      !sessionStorage.getItem("access_token") &&
      !sessionStorage.getItem("code_processed")
    ) {
      sessionStorage.setItem("code_processed", "true");
      dispatch(setLoading(true));
      const code = queryParams.get("code");
      if (code) afterLogin(code);
    }
    if (queryParams.has("error") && !sessionStorage.getItem("access_token")) {
      const transmitError = queryParams.get("error");
      if (transmitError === "invalid code") {
        dispatch(setMagicLink(true));
      }
    }
  }, []);

  // Shared utility for clearing sensitive data
  const clearSensitiveData = useCallback(() => {
    if (mounted.current) {
      dispatch(setPassword(""));
      dispatch(setTwoFACode(""));
    }
  }, [dispatch]);

  // Centralized error handler
  const handleLoginError = useCallback(
    async (err: any, context: string) => {
      const e = err as Error;

      // Turnstile tokens are single-use: drop the spent one so the widget
      // issues a fresh token before the next attempt.
      dispatch(setTurnstileToken(""));

      if (e.message.includes("User requires 2FA!")) {
        console.error(`${context}: User requires 2FA!`);
        dispatch(setShowTwoFA(true));
        return;
      }

      if (err.message.includes("suspended")) {
        console.error(`${context}: User suspended temporarily for too many attempts`);
        dispatch(setPasswordError("Too many tries, please try again after 5 minutes."));
        return;
      }

      if (err.message.includes("Too many failed login attempts")) {
        console.error(`${context}: Per-email login limit reached`);
        dispatch(setPasswordError(err.message));
        return;
      }

      if (err.message.includes("Verification failed")) {
        console.error(`${context}: Turnstile verification failed`);
        dispatch(setPasswordError("Verification failed, please refresh the page and try again."));
        return;
      }

      if (err.message.includes("unverified")) {
        console.error(`${context}: User is unverified but trying to login`, err.message);
        const currentEmail = getSessionItem("email");
        if (currentEmail) {
          await sendMagicLink({
            magicLinkDto: { email: currentEmail, redirectUrl: window.location.origin },
          });
        }
        dispatch(
          setPasswordError(
            "Please verify your email to proceed. You will receive a new verification email from accounts@calebandbrown.com, please check your inbox/spam and follow instructions to complete the verification.",
          ),
        );
        return;
      }

      if (err.message.includes("expired")) {
        const resetToken = JSON.parse(err.message).reset_token;
        dispatch(setResetCode(resetToken));
        dispatch(setError("Password expired, please reset your password"));
        return;
      }

      if (err.message.includes("Invalid credentials")) {
        console.error(`${context}: Invalid user credentials`);
        dispatch(setPassword(""));
        dispatch(setPasswordError("Incorrect email or password"));
        return;
      }

      if (err.message.includes("passkey")) {
        dispatch(setStep(1));
        dispatch(setPassword(""));
        dispatch(setPasswordError("Please try again using a passkey, or contact support"));
        return;
      }

      // Default error handling
      console.error(`${context}: Something went wrong`, err.message);
      dispatch(
        setPasswordError(
          "Something went wrong. Please check your email and password and try again. If you are unsure, please email us at support@calebandbrown.com and describe your issue.",
        ),
      );
    },
    [dispatch, sendMagicLink],
  );

  // Login handlers
  const afterLogin = useCallback(
    async (code: string): Promise<void> => {
      if (code) {
        try {
          await tokenExchange(code);

          window.location.href = "/portfolio";
        } catch (err: any) {
          const e = err as Error;
          if (e.message.includes("User requires 2FA!")) {
            dispatch(setShowTwoFA(true));
          } else {
            if (err.message.includes("suspended")) {
              dispatch(setPasswordError("Too many tries, please try again after 5 minutes."));
            } else if (err.message.includes("unverified")) {
              const currentEmail = getSessionItem("email");
              if (currentEmail) {
                await sendMagicLink({
                  magicLinkDto: { email: currentEmail, redirectUrl: window.location.origin },
                });
              }
            } else {
              console.error(`Invalid user credentials for ${email}`, err.message);
              dispatch(setPasswordError("Invalid user credentials"));
            }
          }
        } finally {
          if (mounted.current) {
            dispatch(setLoading(false));
          }
        }
      }
    },
    [tokenExchange, dispatch, email, sendMagicLink],
  );

  const handleNext = useCallback(async () => {
    const emailRegex = /\S+@\S+\.\S+/;
    if (emailRegex.test(email)) {
      dispatch(setEmailError(""));
      dispatch(setIsNextLoading(true));
      sessionStorage.setItem("email", email);
      try {
        if (isSupported) {
          const webauthnEncodedResult = await window.tsPlatform.webauthn.authenticate.modal(email);
          await webauthnLogin(webauthnEncodedResult);
          const riskScorePasskey = await executeTransmitRisk("login", email);

          // Add a small delay to allow Redux state to be processed
          setTimeout(() => {
            if (mounted.current) {
              loginRedirect();
            }
          }, 100);
        }
      } catch (err: any) {
        const e = err as Error;
        console.error("handleNext error:", e.message);
      } finally {
        if (mounted.current) {
          dispatch(setIsNextLoading(false));
          dispatch(setStep(2));
        }
      }
    } else {
      dispatch(setEmailError("Please enter a valid email address"));
    }
  }, [email, isSupported, webauthnLogin, executeTransmitRisk, loginRedirect, dispatch]);

  const handleLoginSubmit = useCallback(async (): Promise<void> => {
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(email)) {
      dispatch(setEmailError("Please enter a valid email address"));
      return;
    }

    dispatch(setIsSubmitLoading(true));
    email && dispatch(setLoginEmail(email.toLowerCase()));
    setSessionCookie("email", email.toLowerCase());
    try {
      await login(email.toLowerCase(), password, undefined, turnstileToken || undefined);
      await executeTransmitRisk("login", email);

      // Clear sensitive data
      clearSensitiveData();

      // Add a small delay to allow Redux state to be processed
      setTimeout(() => {
        if (mounted.current) {
          loginRedirect();
        }
      }, 100);
    } catch (err: any) {
      await handleLoginError(err, "handleLoginSubmit");
    } finally {
      if (mounted.current) {
        dispatch(setIsSubmitLoading(false));
      }
    }
  }, [
    email,
    password,
    login,
    turnstileToken,
    executeTransmitRisk,
    clearSensitiveData,
    handleLoginError,
    dispatch,
  ]);

  const handleKeyDown = useCallback(
    async (event: any): Promise<void> => {
      const emailRegex = /\S+@\S+\.\S+/;
      if (event.key === "Enter") {
        event.preventDefault();
        if (step === 1 && emailRegex.test(email)) {
          // Call handleNext for step 1 (passkey authentication)
          handleNext();
        } else if (emailRegex.test(email)) {
          // Call handleLoginSubmit for step 2 (password authentication)
          handleLoginSubmit();
        } else {
          dispatch(setEmailError("Please enter a valid email address"));
        }
      }
    },
    [step, email, handleNext, handleLoginSubmit, dispatch],
  );

  const handleDialogClose = useCallback(
    (value: string): void => {
      dispatch(setDialog(false));
      dispatch(setSelectedValue(value));
    },
    [dispatch],
  );

  const handleAccountChange = useCallback(
    async (customerId: string): Promise<void> => {
      dispatch(setLoading(true));

      let body = { customerId: customerId };
      await setSelectedUser({
        customerIdDto: body,
      });
      await setDefaultUser({
        customerIdDto: body,
      });

      // Use loginRedirect instead of window.location.replace to go to the portfolio page
      loginRedirect();
    },
    [dispatch, setSelectedUser, setDefaultUser, loginRedirect],
  );

  // Track events
  const trackForgotPasswordClick = useCallback(() => {
    trackEvent("User redirected to password reset", undefined, "Login");
  }, []);

  const trackSignUpClick = useCallback(() => {
    trackEvent("User redirected to signup", undefined, "Login");
  }, []);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      dispatch(setEmail(value));
      // Clear email error when user starts typing
      if (!!value && emailError) {
        dispatch(setEmailError(""));
      }
    },
    [dispatch, emailError],
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      dispatch(setPassword(value));
      // Clear password error when user starts typing
      if (!!value && passwordError) {
        dispatch(setPasswordError(""));
      }
    },
    [dispatch, passwordError],
  );

  const handleTwoFACodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Only allow numeric characters and limit to 6 digits
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue.length <= 6) {
        dispatch(setTwoFACode(numericValue));
        // Clear 2FA error when user starts typing
        if (!!numericValue && twoFAError) {
          dispatch(setTwoFAError(""));
        }
      }
    },
    [dispatch, twoFAError],
  );

  const handleTwoFASubmit = useCallback(async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      dispatch(setTwoFAError("Please enter a 6-digit code"));
      return;
    }

    try {
      dispatch(setIsTwoFALoading(true));
      dispatch(setTwoFAError(""));

      await login(email.toLowerCase(), password, twoFACode, turnstileToken || undefined);
      await executeTransmitRisk("login", email);
      // Clear sensitive data
      clearSensitiveData();
      // Add a small delay to allow Redux state to be processed
      setTimeout(() => {
        if (mounted.current) {
          loginRedirect();
        }
      }, 100);
    } catch (err: any) {
      console.error("2FA submission error:", err);
      // Spent Turnstile token — let the widget mint a fresh one for the retry.
      dispatch(setTurnstileToken(""));
      if (err.message.includes("Invalid")) {
        dispatch(setTwoFAError("Invalid code, try again"));
      } else {
        dispatch(setTwoFAError("Something went wrong"));
      }
    } finally {
      if (mounted.current) {
        dispatch(setIsTwoFALoading(false));
      }
    }
  }, [
    twoFACode,
    email,
    password,
    login,
    turnstileToken,
    executeTransmitRisk,
    clearSensitiveData,
    dispatch,
  ]);

  const handleTwoFABack = useCallback(() => {
    dispatch(setShowTwoFA(false));
    dispatch(setTwoFACode(""));
    dispatch(setTwoFAError(""));
    dispatch(setStep(1));
    dispatch(setIsSubmitLoading(false));
    dispatch(setPassword(""));
  }, [dispatch]);

  return {
    // State
    email,
    password,
    showPassword,
    resetCode,
    step,
    error,
    isNextLoading,
    isSubmitLoading,
    failedLogin,
    selectedValue,
    dialog,
    magicLink,
    loading,
    noDefault,
    isSupported,
    sdkReady,
    twoFACode,
    twoFAError,
    isTwoFALoading,
    showTwoFA,
    loginEmail,
    device,

    // Actions
    setEmail: useCallback((value: string) => dispatch(setEmail(value)), [dispatch]),
    setEmailError: useCallback((value: string) => dispatch(setEmailError(value)), [dispatch]),
    setPassword: useCallback((value: string) => dispatch(setPassword(value)), [dispatch]),
    setPasswordError: useCallback((value: string) => dispatch(setPasswordError(value)), [dispatch]),
    setShowPassword: useCallback((value: boolean) => dispatch(setShowPassword(value)), [dispatch]),
    setResetCode: useCallback((value: string) => dispatch(setResetCode(value)), [dispatch]),
    setStep: useCallback((value: number) => dispatch(setStep(value)), [dispatch]),
    setError: useCallback((value: string) => dispatch(setError(value)), [dispatch]),
    setIsNextLoading: useCallback(
      (value: boolean) => dispatch(setIsNextLoading(value)),
      [dispatch],
    ),
    setIsSubmitLoading: useCallback(
      (value: boolean) => dispatch(setIsSubmitLoading(value)),
      [dispatch],
    ),
    setFailedLogin: useCallback((value: number) => dispatch(setFailedLogin(value)), [dispatch]),
    setSelectedValue: useCallback((value: string) => dispatch(setSelectedValue(value)), [dispatch]),
    setDialog: useCallback((value: boolean) => dispatch(setDialog(value)), [dispatch]),
    setMagicLink: useCallback((value: boolean) => dispatch(setMagicLink(value)), [dispatch]),
    setLoading: useCallback((value: boolean) => dispatch(setLoading(value)), [dispatch]),
    setNoDefault: useCallback((value: boolean) => dispatch(setNoDefault(value)), [dispatch]),
    setIsSupported: useCallback((value: boolean) => dispatch(setIsSupported(value)), [dispatch]),
    setSdkReady: useCallback((value: boolean) => dispatch(setSdkReady(value)), [dispatch]),
    resetLoginForm: useCallback(() => dispatch(resetLoginForm()), [dispatch]),

    // Handlers
    handleNext,
    handleLoginSubmit,
    handleKeyDown,
    handleEmailChange,
    handlePasswordChange,
    handleTwoFACodeChange,
    handleTwoFASubmit,
    handleTwoFABack,
    afterLogin,
    handleDialogClose,
    handleAccountChange,
    trackForgotPasswordClick,
    trackSignUpClick,
    loginRedirect,
  };
}
